import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  Paper,
  Divider,
  TextField,
  MenuItem,
} from "@mui/material";
import axios from "axios";

export default function GlobalAlerts() {
  const [alerts, setAlerts] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState("ALL");

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
   const res = await axios.get(
  `${import.meta.env.VITE_API_GATEWAY_URL}/api/war-room/alerts`
);
      setAlerts(res.data || []);
    } catch (err) {
      console.error("Failed to fetch alerts", err);
    }
  };

  // -------- Helpers --------
  const getTypeEmoji = (type: string) => {
    if (!type) return null;
    const t = type.toLowerCase();

    if (t.includes("power")) return "⚡";
    if (t.includes("night")) return "🌙";
    if (t.includes("geofence")) return "📍";
    if (t.includes("overspeed")) return "🚗";
    if (t.includes("idle")) return "⏸";
    if (t.includes("harsh")) return "⚠️";

    return "🔔";
  };

  const getSeverityColor = (sev: string) => {
    if (sev === "HIGH") return "error";
    if (sev === "MEDIUM") return "warning";
    return "success";
  };

  // -------- Filtering --------
  const filteredAlerts = useMemo(() => {
    return alerts.filter((a) => {
      const matchesSearch =
        a.type?.toLowerCase().includes(search.toLowerCase()) ||
        a.vehicleId?.toLowerCase().includes(search.toLowerCase()) ||
        a.location?.toLowerCase().includes(search.toLowerCase());

      const matchesSeverity =
        severityFilter === "ALL" || a.severity === severityFilter;

      return matchesSearch && matchesSeverity;
    });
  }, [alerts, search, severityFilter]);

  // -------- Counts --------
  const total = alerts.length;
  const high = alerts.filter(a => a.severity === "HIGH").length;
  const medium = alerts.filter(a => a.severity === "MEDIUM").length;
  const low = alerts.filter(a => a.severity === "LOW").length;

  return (
    <Box p={3}>
      {/* Header */}
      <Typography variant="h6" fontWeight={600} mb={2}>
        Alerts
      </Typography>

      {/* 🔢 Counts (clean row) */}
      <Stack direction="row" spacing={1} mb={2}>
        <Chip label={`Total: ${total}`} variant="outlined" />
        <Chip label={`High: ${high}`} color="error" variant="outlined" />
        <Chip label={`Medium: ${medium}`} color="warning" variant="outlined" />
        <Chip label={`Low: ${low}`} color="success" variant="outlined" />
      </Stack>

      {/* 🔍 Filters */}
      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          size="small"
          placeholder="Search alerts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
        />

        <TextField
          select
          size="small"
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="ALL">All</MenuItem>
          <MenuItem value="HIGH">High</MenuItem>
          <MenuItem value="MEDIUM">Medium</MenuItem>
          <MenuItem value="LOW">Low</MenuItem>
        </TextField>
      </Stack>

      {/* Empty state */}
      {filteredAlerts.length === 0 && (
        <Typography color="text.secondary">No alerts found</Typography>
      )}

      {/* Alerts list */}
      <Stack spacing={2}>
        {filteredAlerts.map((a, i) => (
          <Paper
            key={i}
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              borderLeft: "3px solid #e0e0e0",
            }}
          >
            {/* Top Row */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography>{getTypeEmoji(a.type)}</Typography>

              <Typography fontWeight={600}>
                {a.type?.replace(/_/g, " ")}
              </Typography>

              <Chip
                size="small"
                label={a.severity}
                color={getSeverityColor(a.severity)}
              />
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            {/* Details */}
            <Typography fontWeight={500}>
              {a.vehicleId?.replace("opspod:", "")}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {a.location}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {new Date(a.eventTime).toLocaleString()}
            </Typography>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}